import React, { useState, useEffect } from "react";
import { push } from "connected-react-router";
import useForm from "react-hook-form";
import ErrorMessage from "components/ErrorMessage";
import Input from "components/Input";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Grid from "@material-ui/core/Grid";
import SubmitButton from "components/Button";
import { useTranslation } from "react-i18next";
import Alert from "@material-ui/lab/Alert";
import imgLoading from "asset/img/loading.gif";
import { useDispatch } from "react-redux";
import { SET_MESSAGES_REDUCER } from "redux/reducers/messages/actionTypes";
import { UPDATE_CURRENT_USER } from "redux/reducers/auth/actionTypes";
import { updateMyProfile } from "api/services/getUsers";
import { UserReducer } from "redux/reducers/auth";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Text from "@material-ui/core/Typography";
import EditIcon from "@material-ui/icons/Edit";
import RefreshIcon from "@material-ui/icons/Refresh";
import useStyles from "./styles";
import CustomAvatar from "components/Image/Avatar";
import TextField from "@material-ui/core/TextField";
import LocationModal from "./components/LocationModal";
import AvatarEditModal from "./components/AvatarEditModal";

interface FormMyProfileProps {
  user: UserReducer;
}

const FormMyProfile = React.memo((props: FormMyProfileProps) => {
  const classes = useStyles();
  const translate = useTranslation().t;
  const dispatch = useDispatch();

  const { user } = props;
  const { register, errors, handleSubmit } = useForm({
    defaultValues: user,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>();
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  // Location
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [localLocationText, setLocalLocationText] = useState<string>("");

  const getAvatarUrl = (avatarUrl: string | null): string | null => {
    if (!avatarUrl) return null;
    if (avatarUrl.startsWith("data:") || avatarUrl.startsWith("blob:")) {
      return avatarUrl;
    }
    const baseUrl = process.env.REACT_APP_DOMAIN_API || "";
    return `${baseUrl}${avatarUrl}`;
  };

  useEffect(() => {
    setLocalLocationText(
      typeof (user as any).locationText === "string" ? (user as any).locationText : ""
    );
  }, [user]);

  const handleOpenLocationModal = () => {
    setShowLocationModal(true);
  };

  const handleCloseLocationModal = () => {
    setShowLocationModal(false);
  };

  const handleOpenAvatarModal = () => {
    setAvatarModalOpen(true);
  };

  const handleCloseAvatarModal = () => {
    setAvatarModalOpen(false);
  };

  const submit = async (data: any, e: any) => {
    setLoading(true);
    e.preventDefault();
    const locationTextToSave = localLocationText || "";

    const obj = {
      ...data,
      locationText: locationTextToSave,
      userId: user.id,
    };
    const res = await updateMyProfile(obj);
    if (res && res.id) {
      const updatedUser = {
        ...res,
        locationText:
          res.locationText || obj.locationText || locationTextToSave,
      };
      // dispatch(push("/"));
      dispatch({
        type: UPDATE_CURRENT_USER,
        current_user: updatedUser,
      });
      dispatch({
        type: SET_MESSAGES_REDUCER,
        message: {
          type: "success",
          key: "UPDATE_PROFILE_SUCCESSFULLY",
          message: translate("UPDATE_PROFILE_SUCCESSFULLY"),
        },
      });
    }
    setError("");
    setLoading(false);
  };

  return (
    <React.Fragment>
      <div className={classes.container}>
        {/* Left Section */}
        <div className={classes.leftSection}>
          <form name="form_user" onSubmit={handleSubmit(submit)}>
            <Input
              name="displayName"
              type="string"
              fullWidth
              placeholder={translate("PH_DISPLAY_NAME_PROFILE")}
              label={translate("NAME")}
              validate={register({
                required: true,
              })}
              error={errors.displayName}
            />
            {errors.displayName && errors.displayName.type === "required" && (
              <ErrorMessage>
                {translate("REQUIRED_INPUT_CTA", {
                  value: translate("NAME"),
                })}
              </ErrorMessage>
            )}

            <Input
              name="email"
              type="email"
              fullWidth
              placeholder="mark@gmail.com"
              label={translate("EMAIL")}
              validate={register({
                required: true,
              })}
              error={errors.email}
              disabled
            />

            <Input
              name="phone"
              type="phonenumber"
              fullWidth
              placeholder="(303) 555-0100"
              label={translate("PHONE")}
              validate={register({
                required: false,
              })}
              error={errors.phone}
            />
            {errors.email && errors.email.type === "required" && (
              <ErrorMessage>
                {translate("REQUIRED_INPUT_CTA", {
                  value: translate("PHONE"),
                })}
              </ErrorMessage>
            )}

            {/* Location Field */}
            <Box className={classes.locationFieldContainer}>
              <Box className={classes.locationFieldHeader}>
                <Text className={classes.locationFieldLabel}>
                  {translate("LOCATION")}
                </Text>
                <Box className={classes.locationFieldActions}>
                  <IconButton
                    size="small"
                    onClick={handleOpenLocationModal}
                    className={classes.locationFieldIconButton}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setLocalLocationText("");
                    }}
                    className={classes.locationFieldIconButton}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <TextField
                className={classes.locationInput}
                placeholder={translate("LOCATION")}
                value={localLocationText}
                disabled
                fullWidth
              />
            </Box>

            <Input
              name="role"
              type="string"
              fullWidth
              placeholder=""
              label={translate("ROLE")}
              value={
                user.roles && user.roles.length > 0
                  ? user.roles[0]
                  : translate("USER") || "USER"
              }
              disabled
            />

            {error && <Alert severity="error">{error}</Alert>}
            <Grid
              style={{ paddingTop: "20px" }}
              container
              spacing={1}
              direction="row"
            >
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <ButtonGroup fullWidth>
                  <SubmitButton
                    disabled={loading}
                    type="submit"
                    variant="contained"
                  >
                    {loading ? (
                      <img alt="loading" src={imgLoading} width="30px" />
                    ) : (
                      translate("UPDATE_PROFILE")
                    )}
                  </SubmitButton>
                </ButtonGroup>
              </Grid>
            </Grid>
          </form>

          {/* Change Password */}
          <div className={classes.changePasswordContainer}>
            <span
              className={classes.changePasswordLink}
              onClick={() => dispatch(push("/change_password"))}
            >
              {translate("CHANGE_PASSWORD")}
            </span>
          </div>
        </div>

        {/* Right Section */}
        <div className={classes.rightSection}>
          <div className={classes.avatarContainer}>
            <CustomAvatar
              src={getAvatarUrl(user.imageUrl)}
              alt={user.displayName}
              onClick={handleOpenAvatarModal}
            >
              {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
            </CustomAvatar>
            <Button
              variant="outlined"
              className={classes.editButton}
              startIcon={<EditIcon />}
              onClick={handleOpenAvatarModal}
            >
              {translate("EDIT_AVATAR")}
            </Button>
          </div>
        </div>
      </div>

      <AvatarEditModal
        open={avatarModalOpen}
        onClose={handleCloseAvatarModal}
        user={user}
        localLocationText={localLocationText}
      />

      <LocationModal
        open={showLocationModal}
        onClose={handleCloseLocationModal}
        onSaveLocation={(locationText: string) => {
          setLocalLocationText(locationText);
        }}
      />
    </React.Fragment>
  );
});

export default FormMyProfile;